import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "./AuthProvider";
import {
  addIssueComment,
  assignIssue,
  backlog,
  changeIssueStatus,
  changeIssueType,
  completeSprint,
  createIssue,
  createSprint,
  deleteIssue,
  deleteSprint,
  friendlyMessage,
  getIssue,
  issueActivity,
  kanbanBoard,
  listIssueComments,
  listIssues,
  listSprints,
  listSubtasks,
  moveIssueToSprint,
  setIssueLabels,
  setIssuePoints,
  setIssueRank,
  setIssueWorkAreas,
  sprintBurndown,
  startSprint,
  updateIssue,
  updateSprint,
  watchIssue,
} from "./projectApi";
import type {
  AddIssueCommentRequest,
  BurndownChart,
  CreateIssueRequest,
  CreateSprintRequest,
  Issue,
  IssueActivity,
  IssueComment,
  IssuePriority,
  IssueStatus,
  IssueType,
  Sprint,
  StartSprintRequest,
  UpdateIssueRequest,
  UpdateSprintRequest,
} from "../models/project";

/** Which nested screen the issues section is currently showing. */
export type IssuesScreen = "board" | "list" | "backlog" | "detail";

// Orchestration + data for a project's issue tracker: the full issue list, the kanban board, the
// backlog + sprints (with burndown), and the currently-selected issue's detail (comments + activity
// timeline). Scoped to a single projectId; an optional workArea scopes the `issues` list to issues
// whose workAreaTypes contain that area (server-side ?workArea= filter).
export function useIssues(projectId: string | null, workArea?: string) {
  const { token } = useAuth();
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const getToken = useMemo(() => () => tokenRef.current(), []);

  const [screen, setScreen] = useState<IssuesScreen>("board");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [issues, setIssues] = useState<Issue[]>([]);
  const [kanban, setKanban] = useState<Issue[]>([]);
  const [backlogIssues, setBacklogIssues] = useState<Issue[]>([]);
  const [sprints, setSprints] = useState<Sprint[]>([]);

  const [burndownBySprintId, setBurndownBySprintId] = useState<Record<string, BurndownChart>>({});

  const [selectedIssueId, setSelectedIssueId] = useState<string | null>(null);
  // patchIssueEverywhere runs from callbacks created before a later selection; the ref keeps it
  // asking about the issue that is open NOW.
  const selectedIssueIdRef = useRef(selectedIssueId);
  selectedIssueIdRef.current = selectedIssueId;
  const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null);
  const [comments, setComments] = useState<IssueComment[]>([]);
  const [activity, setActivity] = useState<IssueActivity[]>([]);
  const [subtasks, setSubtasks] = useState<Issue[]>([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const reload = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const [all, board, back, spr] = await Promise.all([
        listIssues(getToken, projectId, workArea),
        kanbanBoard(getToken, projectId),
        backlog(getToken, projectId),
        listSprints(getToken, projectId),
      ]);
      setIssues(all);
      setKanban(board);
      setBacklogIssues(back);
      setSprints(spr);
    } catch (e) {
      setError(friendlyMessage(e));
    } finally {
      setLoading(false);
    }
  }, [getToken, projectId, workArea]);

  useEffect(() => {
    if (projectId) void reload();
  }, [projectId, reload]);

  const activeSprint = sprints.find((s) => s.status === "ACTIVE") ?? null;

  function patchIssueEverywhere(updated: Issue) {
    const patch = (list: Issue[]) => list.map((i) => (i.id === updated.id ? updated : i));
    setIssues(patch);
    setKanban(patch);
    setBacklogIssues((cur) => (updated.sprintId ? cur.filter((i) => i.id !== updated.id) : patch(cur)));
    setSelectedIssue((cur) => (cur && cur.id === updated.id ? updated : cur));
    // Every field change (status/assignee/type/points/sprint/…) funnels through here, and the server
    // records one IssueActivity for each. Only addComment used to refetch the history, so the 활동 내역
    // on an open issue silently stopped at "생성됨" no matter how much the issue then changed.
    if (updated.id === selectedIssueIdRef.current) {
      void issueActivity(getToken, updated.id).then(setActivity).catch(() => {});
    }
  }

  async function addIssue(req: CreateIssueRequest): Promise<Issue | null> {
    if (!projectId) return null;
    setError(null);
    try {
      const created = await createIssue(getToken, projectId, req);
      await reload();
      return created;
    } catch (e) {
      setError(friendlyMessage(e));
      return null;
    }
  }

  async function saveIssue(id: string, req: UpdateIssueRequest) {
    setError(null);
    try {
      patchIssueEverywhere(await updateIssue(getToken, id, req));
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  async function setStatus(id: string, status: IssueStatus) {
    setError(null);
    try {
      patchIssueEverywhere(await changeIssueStatus(getToken, id, status));
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  async function setAssignee(id: string, assigneeId: string | null) {
    setError(null);
    try {
      patchIssueEverywhere(await assignIssue(getToken, id, assigneeId));
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  async function setType(id: string, type: IssueType) {
    setError(null);
    try {
      patchIssueEverywhere(await changeIssueType(getToken, id, type));
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  async function setPriorityViaUpdate(id: string, priority: IssuePriority) {
    setError(null);
    try {
      patchIssueEverywhere(await updateIssue(getToken, id, { priority }));
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  async function setPoints(id: string, storyPoints: number | null) {
    setError(null);
    try {
      patchIssueEverywhere(await setIssuePoints(getToken, id, storyPoints));
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  async function moveToSprint(id: string, sprintId: string | null) {
    setError(null);
    try {
      const updated = await moveIssueToSprint(getToken, id, sprintId);
      patchIssueEverywhere(updated);
      await reload();
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  async function setLabels(id: string, labels: string[]) {
    setError(null);
    try {
      patchIssueEverywhere(await setIssueLabels(getToken, id, labels));
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  async function setRank(id: string, rank: string) {
    setError(null);
    try {
      patchIssueEverywhere(await setIssueRank(getToken, id, rank));
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  async function setWorkAreas(id: string, workAreaTypes: string[]) {
    setError(null);
    try {
      patchIssueEverywhere(await setIssueWorkAreas(getToken, id, workAreaTypes));
      // If this list is scoped to a work area, the issue may have moved in/out of scope.
      if (workArea) await reload();
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  async function toggleWatch(id: string, watch: boolean) {
    setError(null);
    try {
      patchIssueEverywhere(await watchIssue(getToken, id, watch));
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  async function removeIssue(id: string) {
    setError(null);
    try {
      await deleteIssue(getToken, id);
      setIssues((cur) => cur.filter((i) => i.id !== id));
      setKanban((cur) => cur.filter((i) => i.id !== id));
      setBacklogIssues((cur) => cur.filter((i) => i.id !== id));
      if (selectedIssueId === id) backToBoard();
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  const loadDetail = useCallback(
    async (id: string) => {
      setLoadingDetail(true);
      setError(null);
      try {
        const [issue, cmts, hist, subs] = await Promise.all([
          getIssue(getToken, id),
          listIssueComments(getToken, id),
          issueActivity(getToken, id),
          listSubtasks(getToken, id),
        ]);
        setSelectedIssue(issue);
        setComments(cmts);
        setActivity(hist);
        setSubtasks(subs);
      } catch (e) {
        setError(friendlyMessage(e));
      } finally {
        setLoadingDetail(false);
      }
    },
    [getToken],
  );

  function openIssue(id: string) {
    setSelectedIssueId(id);
    setScreen("detail");
    void loadDetail(id);
  }

  function backToBoard() {
    setScreen("board");
    setSelectedIssueId(null);
    setSelectedIssue(null);
    setComments([]);
    setActivity([]);
    setSubtasks([]);
  }

  async function addComment(req: AddIssueCommentRequest) {
    if (!selectedIssueId) return;
    setError(null);
    try {
      const cmt = await addIssueComment(getToken, selectedIssueId, req);
      setComments((cur) => [...cur, cmt]);
      setActivity(await issueActivity(getToken, selectedIssueId));
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  // ---- sprints ----
  async function addSprint(req: CreateSprintRequest): Promise<Sprint | null> {
    if (!projectId) return null;
    setError(null);
    try {
      const sprint = await createSprint(getToken, projectId, req);
      setSprints((cur) => [...cur, sprint]);
      return sprint;
    } catch (e) {
      setError(friendlyMessage(e));
      return null;
    }
  }

  async function saveSprint(id: string, req: UpdateSprintRequest) {
    setError(null);
    try {
      const updated = await updateSprint(getToken, id, req);
      setSprints((cur) => cur.map((s) => (s.id === id ? updated : s)));
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  async function startSprintNow(id: string, req: StartSprintRequest = {}) {
    setError(null);
    try {
      const updated = await startSprint(getToken, id, req);
      setSprints((cur) => cur.map((s) => (s.id === id ? updated : s)));
      await reload();
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  async function completeSprintNow(id: string) {
    setError(null);
    try {
      const updated = await completeSprint(getToken, id);
      setSprints((cur) => cur.map((s) => (s.id === id ? updated : s)));
      await reload();
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  async function removeSprint(id: string) {
    setError(null);
    try {
      await deleteSprint(getToken, id);
      setSprints((cur) => cur.filter((s) => s.id !== id));
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  async function loadBurndown(sprintId: string) {
    setError(null);
    try {
      const chart = await sprintBurndown(getToken, sprintId);
      setBurndownBySprintId((cur) => ({ ...cur, [sprintId]: chart }));
      return chart;
    } catch (e) {
      setError(friendlyMessage(e));
      return null;
    }
  }

  return {
    getToken,
    projectId,
    screen,
    setScreen,
    error,
    clearError: () => setError(null),
    loading,

    issues,
    kanban,
    backlogIssues,
    sprints,
    activeSprint,
    burndownBySprintId,
    reload,

    addIssue,
    saveIssue,
    setStatus,
    setAssignee,
    setType,
    setPriority: setPriorityViaUpdate,
    setPoints,
    moveToSprint,
    setLabels,
    setRank,
    setWorkAreas,
    toggleWatch,
    removeIssue,

    selectedIssueId,
    selectedIssue,
    comments,
    activity,
    subtasks,
    loadingDetail,
    openIssue,
    backToBoard,
    addComment,

    addSprint,
    saveSprint,
    startSprintNow,
    completeSprintNow,
    removeSprint,
    loadBurndown,
  };
}
